import { SectionPreviewPane } from '@/components/common/section-preview-pane';
import { ZyPreviewButton } from '@/components/common/zypreview-button';
import { AppPageLayout } from '@/components/layout/app-page-layout';
import { GlobalTopBar } from '@/components/layout/global-top-bar';
import { TiptapEditor, type ExtensionOptions, type TiptapOnCreateCallbackProps } from '@/components/tiptap-editor/tiptap-editor';
import { useZySectionLivePreview } from '@/hooks/use-zy-section-live-preview';
import { getMaImagesLibraryBaseUrl } from '@/lib/ma-images-library';
import { zyAuthor } from '@/lib/servers';
import { setDocAttributes } from '@/lib/zy-tiptap-utils';
import { resolveInitialZyVersionFilter } from '@/lib/zy-tiptap-utils-core';
import { getDocumentZyVersions, getMaDocument, type GetMaDocumentResponse } from '@/lib/zybooks-api';
import { Editor } from '@tiptap/react';
import { useEffect, useMemo, useState } from 'react';
import { Panel, PanelGroup } from 'react-resizable-panels';
import { useParams, useSearchParams } from 'react-router';

export default function Document() {
    const { documentId: documentIdString } = useParams();
    const [searchParams] = useSearchParams();
    const zybookCodeParam = searchParams.get('zybookCode')?.trim() ?? '';
    const documentId = useMemo(() => {
        const n = documentIdString != null ? Number(documentIdString) : NaN;
        return Number.isInteger(n) && n > 0 ? n : null;
    }, [documentIdString]);

    const [editor, setEditor] = useState<Editor | null>(null);
    const [maDoc, setMaDoc] = useState<GetMaDocumentResponse | null>(null);
    const [maLoadError, setMaLoadError] = useState<string | null>(null);
    const [validVersions, setValidVersions] = useState<string[] | null>(null);
    const [devBookInfo, setDevBookInfo] = useState<{
        content_source: string;
        release_number: number;
        version: string | null;
    } | null>(null);

    const { iframeRef, iframeURL, connectionError, tiptapEditorRef, previewMode, previewError, isRenderingOutput, togglePreviewMode } =
        useZySectionLivePreview(editor, { documentKey: maDoc?.document_name ?? String(documentId ?? '') });

    useEffect(() => {
        if (documentId == null) {
            setMaDoc(null); // eslint-disable-line react-hooks/set-state-in-effect
            setValidVersions(null);
            setDevBookInfo(null);
            setMaLoadError('Invalid document id.');
            return;
        }
        let cancelled = false;
        setMaLoadError(null);
        setValidVersions(null);
        setDevBookInfo(null);
        (async () => {
            try {
                const ma = await getMaDocument(documentId);
                const devBookName = zybookCodeParam !== '' ? zybookCodeParam : ma.ma_zybook_documents[0]?.zybook_code;
                if (!devBookName) {
                    if (!cancelled) {
                        setMaLoadError('No zybook section associated with this document.');
                    }
                    return;
                }
                const [{ versions }, documentInfoData] = await Promise.all([
                    getDocumentZyVersions(ma.ma_document_id),
                    zyAuthor.get<{ content_source: string; release_number: number; version: string | null }>(
                        `modern_authoring/get_devbook_info?devBookName=${encodeURIComponent(devBookName)}`,
                    ),
                ]);
                if (!cancelled) {
                    setMaDoc(ma);
                    setValidVersions(versions);
                    setDevBookInfo(documentInfoData);
                    setMaLoadError(null);
                }
            } catch (e) {
                if (!cancelled) {
                    setMaDoc(null);
                    setValidVersions(null);
                    setDevBookInfo(null);
                    setMaLoadError((e as Error).message ?? 'Failed to load document.');
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [documentId, zybookCodeParam]);

    useEffect(() => {
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        if (!maDoc || !editor || !devBookInfo) {
            return;
        }
        const baseImageUrl = getMaImagesLibraryBaseUrl();
        setDocAttributes(editor, {
            maDocumentId: maDoc.ma_document_id,
            baseImageUrl,
            sectionId: maDoc.section_guid,
            releaseId: devBookInfo.release_number,
            subjectName: devBookInfo.content_source,
            documentId: maDoc.ma_document_id,
        });
    }, [devBookInfo, maDoc, editor]);

    const handleTipTapCreate = ({ editor }: TiptapOnCreateCallbackProps) => {
        setEditor(editor);
    };

    const showZyVersionToolbar = (validVersions?.length ?? 0) > 0;

    const extensionOptions = useMemo((): ExtensionOptions | undefined => {
        if (validVersions == null || validVersions.length === 0) {
            return undefined;
        }
        return {
            zyVersion: {
                allowedVersions: validVersions,
                versionFilter: resolveInitialZyVersionFilter(validVersions, devBookInfo?.version),
            },
        };
    }, [devBookInfo, validVersions]);

    const headerTitle = useMemo(() => {
        if (!Number.isFinite(documentId)) {
            return 'Invalid document id';
        }
        if (maLoadError) {
            return 'Invalid document';
        }
        if (!maDoc) {
            return 'Loading...';
        }

        const placements = maDoc.ma_zybook_documents ?? [];
        const byParam = zybookCodeParam !== '' ? placements.find(p => p.zybook_code === zybookCodeParam) : undefined;
        const placement = byParam ?? placements[0];
        if (!placement) {
            return `Section ${maDoc.section_guid}`;
        }
        return `${placement.zybook_code}: ${placement.chapter_number}.${placement.section_number} ${placement.section_title}`;
    }, [documentId, maLoadError, maDoc, zybookCodeParam]);

    const editorReady = maDoc != null && validVersions != null && devBookInfo != null;

    return (
        <AppPageLayout className="flex h-screen flex-col overflow-hidden">
            <GlobalTopBar.Start>
                <h1 className="text-2xl font-bold">{headerTitle}</h1>
            </GlobalTopBar.Start>
            <GlobalTopBar.Actions>
                <ZyPreviewButton
                    editor={editor}
                    previewMode={previewMode}
                    disabled={isRenderingOutput}
                    onTogglePreview={togglePreviewMode}
                />
            </GlobalTopBar.Actions>

            <AppPageLayout.Body className="min-h-0 flex-1 overflow-hidden">
                <PanelGroup direction="vertical">
                    <Panel
                        defaultSize={70}
                        minSize={20}
                    >
                        <div className="flex h-full w-full min-w-0">
                            <SectionPreviewPane
                                visible={previewMode}
                                iframeRef={iframeRef}
                                iframeURL={iframeURL}
                                previewError={previewError}
                                connectionError={connectionError}
                            />

                            <div
                                className={`w-full min-w-0 ${previewMode ? 'hidden' : ''}`}
                                aria-hidden={previewMode}
                            >
                                {editorReady ? (
                                    <TiptapEditor
                                        ref={tiptapEditorRef}
                                        content={`Opening document...`}
                                        useCollaboration={true}
                                        onCreate={handleTipTapCreate}
                                        tiptapDocumentName={maDoc.document_name}
                                        extensionOptions={extensionOptions}
                                        showZyVersionToolbar={showZyVersionToolbar}
                                    />
                                ) : (
                                    !maLoadError && (
                                        <div className="text-muted-foreground flex h-full items-center justify-center text-sm">
                                            Loading document…
                                        </div>
                                    )
                                )}
                            </div>
                        </div>
                    </Panel>
                </PanelGroup>

                {maLoadError && <p className="text-destructive px-6 text-sm">{maLoadError}</p>}
            </AppPageLayout.Body>
        </AppPageLayout>
    );
}
